import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, parse } from "date-fns";

// Function to calculate availability percentage and determine color
const getAvailabilityColor = (estimated, installed, pending) => {
  const totalUsed = (installed || 0) + (pending || 0);
  const available = (estimated || 0) - totalUsed;
  const percentage = estimated > 0 ? (available / estimated) * 100 : 0;

  if (percentage >= 60) {
    return {
      color: "bg-green-400",
      textColor: "text-green-400",
      percentage,
      available,
    };
  } else if (percentage >= 20) {
    return {
      color: "bg-yellow-400",
      textColor: "text-yellow-400",
      percentage,
      available,
    };
  } else {
    return {
      color: "bg-red-400",
      textColor: "text-red-400",
      percentage,
      available,
    };
  }
};

const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-red-400 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-orange-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-orange-400 hover:bg-orange-500",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-10 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-orange-400"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm sm:w-auto transition-colors duration-200`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const PieceworkerLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [log, setLog] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [availableLineItems, setAvailableLineItems] = useState([]);
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [bidAreaFilter, setBidAreaFilter] = useState("");
  const [bidAreas, setBidAreas] = useState([]);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    const fetchLogAndWorkOrder = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Authentication token not found");

        // Fetch pieceworker log
        const logResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        console.log("Log Response:", logResponse.data);
        setLog(logResponse.data);
        setSelectedEmployeeId(
          logResponse.data.user?._id || logResponse.data.user,
        );

        // Fetch associated work order
        const workOrderResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/workOrders/${
            logResponse.data.workOrder._id || logResponse.data.workOrder
          }`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        console.log("Work Order Response:", workOrderResponse.data);
        setWorkOrder(workOrderResponse.data);

        // Fetch pieceworkers
        const employeesResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/users/pieceworkers`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        console.log("Employees Response:", employeesResponse.data);
        setEmployees(employeesResponse.data);

        // Initialize quantities from log's lineItems
        const initialQuantities = {};
        logResponse.data.lineItems?.forEach((item) => {
          const lineItemId = item.lineItemId.$oid || item.lineItemId;
          initialQuantities[lineItemId] = item.qtyInstalled || 0;
        });
        setQuantities(initialQuantities);

        // Set available line items (those not already in the log)
        const existingLineItemIds =
          logResponse.data.lineItems?.map(
            (item) => item.lineItemId.$oid || item.lineItemId,
          ) || [];
        const availableItems =
          workOrderResponse.data.lineItems?.filter(
            (item) => !existingLineItemIds.includes(item._id.$oid || item._id),
          ) || [];
        setAvailableLineItems(availableItems);

        // Extract unique bid areas
        const uniqueBidAreas = [
          ...new Set(
            workOrderResponse.data.lineItems
              ?.map((item) => item.bidArea)
              .filter((bidArea) => bidArea),
          ),
        ];
        setBidAreas(uniqueBidAreas);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load pieceworker log or work order.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchLogAndWorkOrder();
  }, [id]);

  const handleQuantityChange = (lineItemId, value) => {
    const numValue = parseFloat(value) || 0;
    const logItem = log?.lineItems?.find(
      (item) => (item.lineItemId.$oid || item.lineItemId) === lineItemId,
    );
    const workOrderItem = workOrder?.lineItems?.find(
      (item) => (item._id.$oid || item._id) === lineItemId,
    );

    if (workOrderItem) {
      const remainingQty =
        (workOrderItem.qtyEstimated || 0) -
        (workOrderItem.qtyInstalled || 0) -
        (workOrderItem.qtyPending || 0) +
        (logItem?.qtyInstalled || 0);
      if (numValue > remainingQty) {
        setDialog({
          open: true,
          type: "error",
          title: "Invalid Quantity",
          message: `Quantity cannot exceed ${remainingQty} ${
            logItem?.uom || workOrderItem.uom
          }.`,
          onConfirm: () => setDialog({ ...dialog, open: false }),
        });
        return;
      }
    }

    setQuantities((prev) => ({
      ...prev,
      [lineItemId]: numValue,
    }));
  };

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  const handleAddLineItem = (lineItem) => {
    const lineItemId = lineItem._id.$oid || lineItem._id;
    setSelectedLineItems((prev) => [
      ...prev,
      {
        lineItemId,
        title: lineItem.title || "Untitled",
        qtyInstalled: 0,
        uom: lineItem.uom,
        unitCost: lineItem.unitCost || 0,
        bidArea: lineItem.bidArea,
      },
    ]);
    setQuantities((prev) => ({
      ...prev,
      [lineItemId]: 0,
    }));
    setAvailableLineItems((prev) =>
      prev.filter((item) => (item._id.$oid || item._id) !== lineItemId),
    );
  };

  const handleEmployeeChange = async (employeeId) => {
    try {
      const token = localStorage.getItem("token");
      const updatedLog = {
        ...log,
        employeeId: employeeId,
      };

      await axios.patch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}`,
        updatedLog,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSelectedEmployeeId(employeeId);
      setLog((prev) => ({
        ...prev,
        user: employees.find((emp) => emp._id === employeeId),
      }));
      setDialog({
        open: true,
        type: "success",
        title: "Employee Updated",
        message: "The pieceworker has been successfully updated.",
        onConfirm: () => setDialog({ ...dialog, open: false }),
      });
    } catch (err) {
      console.error("Error updating employee:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Update Failed",
        message: "Failed to update pieceworker.",
        onConfirm: () => setDialog({ ...dialog, open: false }),
      });
    }
  };

  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      const updatedLineItems = [
        ...(log?.lineItems?.map((item) => {
          const lineItemId = item.lineItemId.$oid || item.lineItemId;
          return {
            ...item,
            qtyInstalled: parseFloat(
              quantities[lineItemId] ?? item.qtyInstalled ?? 0, // changed || to ??
            ),
          };
        }) || []),
        ...selectedLineItems.map((item) => ({
          lineItemId: item.lineItemId,
          title: item.title,
          qtyInstalled: parseFloat(quantities[item.lineItemId] || 0),
          uom: item.uom,
          unitCost: item.unitCost || 0,
          bidArea: item.bidArea,
        })),
      ].filter((item) => item.qtyInstalled > 0);

      if (updatedLineItems.length === 0) {
        setDialog({
          open: true,
          type: "error",
          title: "No Valid Line Items",
          message:
            "At least one line item with a quantity greater than 0 is required.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      const updatedLog = {
        ...log,
        lineItems: updatedLineItems,
        employeeId: selectedEmployeeId,
      };

      await axios.patch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}`,
        updatedLog,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Re-fetch log and work order to get clean server state
      const [logRes, woRes] = await Promise.all([
        axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/workOrders/${
            log.workOrder._id || log.workOrder
          }`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);

      setLog(logRes.data);
      setWorkOrder(woRes.data);

      // Reset quantities from fresh log data
      const freshQuantities = {};
      logRes.data.lineItems?.forEach((item) => {
        const lineItemId = item.lineItemId.$oid || item.lineItemId;
        freshQuantities[lineItemId] = item.qtyInstalled || 0;
      });
      setQuantities(freshQuantities);

      // Reset available line items (items not in the log)
      const existingIds =
        logRes.data.lineItems?.map(
          (item) => item.lineItemId.$oid || item.lineItemId,
        ) || [];
      const availableItems =
        woRes.data.lineItems?.filter(
          (item) => !existingIds.includes(item._id.$oid || item._id),
        ) || [];
      setAvailableLineItems(availableItems);

      setEditMode(false);
      setSelectedLineItems([]);
      setDialog({
        open: true,
        type: "success",
        title: "Worksheet Updated",
        message: "The pieceworker worksheet has been successfully updated.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      console.error("Error updating pieceworker log:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Update Failed",
        message: "Failed to update pieceworker log.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const updatedLineItems = [
        ...(log?.lineItems?.map((item) => {
          const lineItemId = item.lineItemId.$oid || item.lineItemId;
          return {
            ...item,
            qtyInstalled: parseFloat(
              quantities[lineItemId] ?? item.qtyInstalled ?? 0, // changed || to ??
            ),
          };
        }) || []),
        ...selectedLineItems.map((item) => ({
          lineItemId: item.lineItemId,
          title: item.title,
          qtyInstalled: parseFloat(quantities[item.lineItemId] || 0),
          uom: item.uom,
          unitCost: item.unitCost || 0,
          bidArea: item.bidArea,
        })),
      ].filter((item) => item.qtyInstalled > 0);

      const updatedLog = {
        ...log,
        lineItems: updatedLineItems,
        status: newStatus,
        employeeId: selectedEmployeeId,
      };

      const endpoint =
        newStatus === "approved"
          ? `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}/approve`
          : newStatus === "rejected"
            ? `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}/reject`
            : `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${id}/reactivate`;

      await axios.post(endpoint, updatedLog, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const tabFromStatus =
        {
          pending: "Pending",
          approved: "Approved",
          rejected: "Rejected",
        }[newStatus] || "Pending";

      const targetStatusTab = location.state?.activeStatusTab || tabFromStatus;
      const projectId = log?.project?._id || location.state?.projectId;

      if (projectId && !location.state?.fromMainTab) {
        navigate(`/Projects/${projectId}`, {
          state: {
            mainTab: location.state?.mainTab || "Operations",
            initialTab: location.state?.activeTab || "Worksheets",
            activeStatusTab: targetStatusTab,
          },
        });
      } else {
        navigate("/pieceworkerlog", {
          state: { activeTab: targetStatusTab, fromLogs: true },
        });
      }
    } catch (err) {
      console.error("Error updating pieceworker log:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Update Failed",
        message: "Failed to update pieceworker log.",
        onConfirm: () => setDialog({ ...dialog, open: false }),
      });
    }
  };

  const showConfirmationDialog = (status) => {
    setDialog({
      open: true,
      type: status === "approved" ? "success" : "error",
      title: `Confirm ${
        status === "approved"
          ? "Approval"
          : status === "rejected"
            ? "Rejection"
            : "Reactivation"
      }`,
      message: `Are you sure you want to ${
        status === "approved"
          ? "approve"
          : status === "rejected"
            ? "reject"
            : "reactivate"
      } this pieceworker log with the current quantities and assigned employee?`,
      onConfirm: () => {
        handleUpdateStatus(status);
        setDialog({ ...dialog, open: false });
      },
    });
  };

  const closeDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  const handleBack = () => {
    const targetStatusTab =
      location.state?.activeStatusTab ||
      location.state?.activeTab ||
      {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      }[log?.status] ||
      "Pending";
    const projectId = log?.project?._id || location.state?.projectId;

    console.log("handleBack navigation state:", {
      fromProject: location.state?.fromProject,
      fromMainTab: location.state?.fromMainTab,
      projectId,
      mainTab: location.state?.mainTab,
      activeTab: location.state?.activeTab,
      activeStatusTab: targetStatusTab,
    });

    if (location.state?.fromMainTab) {
      navigate("/pieceworkerlog", {
        state: {
          activeTab: targetStatusTab,
          currentPage: location.state?.currentPage || 1,
        },
      });
    } else if (projectId && location.state?.fromProject) {
      navigate(`/Projects/${projectId}`, {
        state: {
          mainTab: location.state?.mainTab || "Operations",
          initialTab: location.state?.activeTab || "Worksheets",
          activeStatusTab: targetStatusTab,
        },
      });
    } else {
      navigate("/pieceworkerlog", {
        state: { activeTab: targetStatusTab },
      });
    }
  };

  // Calculate total cost safely
  // NEW - recalculates using site rate * edited qty
  const totalPayroll = [...(log?.lineItems || []), ...selectedLineItems]
    .reduce((sum, item) => {
      const lineItemId = item.lineItemId.$oid || item.lineItemId;
      const qty = parseFloat(quantities[lineItemId] ?? item.qtyInstalled ?? 0);
      if (qty <= 0) return sum;
      const originalQty = parseFloat(item.qtyInstalled || 0);
      const perUnitPayroll =
        item.subtotal !== undefined && originalQty > 0
          ? parseFloat(item.subtotal) / originalQty
          : parseFloat(item.unitCost || 0);
      const roundedRate = Math.round(perUnitPayroll * 100) / 100;
      return sum + parseFloat((roundedRate * qty).toFixed(2));
    }, 0)
    .toFixed(2);

  // Calculate total cost safely
  const totalCost = [...(log?.lineItems || []), ...selectedLineItems]
    .reduce((sum, item) => {
      const lineItemId = item.lineItemId.$oid || item.lineItemId;
      const qty = parseFloat(quantities[lineItemId] ?? item.qtyInstalled ?? 0);
      const unitCost = parseFloat(item.unitCost || 0);
      return sum + parseFloat((qty * unitCost).toFixed(2));
    }, 0)
    .toFixed(2);
  // Filter line items safely
  const filteredLineItems = [
    ...(log?.lineItems || []),
    ...selectedLineItems,
  ].filter((item) => !bidAreaFilter || item.bidArea === bidAreaFilter);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-gray-200 text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  if (!log || !workOrder) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">Data not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-700">
      <div className="container mx-auto px-4 py-6 max-w-8xl">
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            <ArrowLeftIcon className="size-4 mr-2" />
            Back to Worksheets
          </button>
        </div>
        <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-orange-400">
                Worksheet Details
              </h1>
              {log.status === "pending" && !editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 text-sm font-semibold text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Edit Worksheet
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Work Order
                </p>
                <p className="text-sm text-gray-200">{log.woNumber}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Status
                </p>
                <p
                  className={`text-sm font-medium ${
                    log.status === "approved"
                      ? "text-green-400"
                      : log.status === "rejected"
                        ? "text-red-400"
                        : "text-yellow-400"
                  }`}
                >
                  {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Work Period
                </p>
                <p className="text-sm text-gray-200">
                  {format(
                    parse(
                      log.workCompletedDate.split("T")[0],
                      "yyyy-MM-dd",
                      new Date(),
                    ),
                    "MMMM d, yyyy",
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Submitted
                </p>
                <p className="text-sm text-gray-200">
                  {format(parseISO(log.submittedAt), "MMMM d, yyyy, h:mm a")}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Employee
                </p>
                {editMode ? (
                  <select
                    value={selectedEmployeeId || ""}
                    onChange={(e) => handleEmployeeChange(e.target.value)}
                    className="block w-full max-w-md text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="" disabled>
                      Select an employee
                    </option>
                    {employees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.displayName || employee.email}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-200">
                    {log.user?.name || log.user?.email}
                  </p>
                )}
              </div>
            </div>

            {log.status === "pending" && editMode && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-orange-400 mb-4">
                  Add Line Items
                </h2>
                <select
                  value=""
                  onChange={(e) => {
                    const selectedItem = availableLineItems.find(
                      (item) => (item._id.$oid || item._id) === e.target.value,
                    );
                    if (selectedItem) handleAddLineItem(selectedItem);
                  }}
                  className="block w-full max-w-md text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="" disabled>
                    Select a line item
                  </option>
                  {availableLineItems.map((item) => (
                    <option
                      key={item._id.$oid || item._id}
                      value={item._id.$oid || item._id}
                    >
                      {item.title || "Untitled"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-orange-400">
                Line Items
              </h2>
              <div>
                <select
                  value={bidAreaFilter}
                  onChange={(e) => setBidAreaFilter(e.target.value)}
                  className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Bid Areas</option>
                  {bidAreas.map((bidArea) => (
                    <option key={bidArea} value={bidArea}>
                      {bidArea}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg shadow-xl">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                      Title
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Bid Area
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Est.
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Inst.
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Pend.
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Avail.
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Edit
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      UOM
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Unit Cost
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Site Rate
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                      Burden Amount
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Payroll Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {filteredLineItems.length > 0 ? (
                    filteredLineItems.map((item, index) => {
                      const lineItemId =
                        item.lineItemId.$oid || item.lineItemId;
                      const workOrderItem = workOrder?.lineItems?.find(
                        (woItem) =>
                          (woItem._id.$oid || woItem._id) === lineItemId,
                      );
                      const { color, textColor, percentage, available } =
                        getAvailabilityColor(
                          workOrderItem?.qtyEstimated,
                          workOrderItem?.qtyInstalled,
                          workOrderItem?.qtyPending,
                        );
                      // Calculate site rate: subtotal / qtyInstalled for existing items, or compute for new items
                      const qty = parseFloat(
                        quantities[lineItemId] ?? item.qtyInstalled ?? 0,
                      );
                      const originalQty = parseFloat(item.qtyInstalled || 0);
                      const siteRate =
                        item.subtotal !== undefined && originalQty > 0
                          ? (item.subtotal / originalQty).toFixed(2)
                          : (item.unitCost || 0).toFixed(2);

                      return (
                        <tr
                          key={lineItemId}
                          className={`${
                            index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"
                          } hover:bg-gray-500 transition-colors duration-200`}
                        >
                          <td className="px-4 py-2 text-sm text-gray-200">
                            {item.title}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            {item.bidArea || "N/A"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            {(
                              workOrderItem?.qtyEstimated || 0
                            ).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            {(
                              workOrderItem?.qtyInstalled || 0
                            ).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            {(workOrderItem?.qtyPending || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span className={textColor}>
                              {available.toLocaleString()} (
                              {percentage.toFixed(1)}%)
                            </span>
                            <div className="w-24 bg-gray-600 rounded-full h-1.5 mt-1">
                              <div
                                className={`${color} h-1.5 rounded-full`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            <input
                              type="text"
                              value={
                                quantities[lineItemId] !== undefined
                                  ? quantities[lineItemId]
                                  : (item.qtyInstalled || 0).toFixed(2)
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === "" ||
                                  /^[0-9]*\.?[0-9]{0,2}$/.test(value)
                                ) {
                                  handleQuantityChange(lineItemId, value);
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value !== "") {
                                  handleQuantityChange(
                                    lineItemId,
                                    Number(value).toFixed(2),
                                  );
                                }
                              }}
                              pattern="[0-9]*(\.[0-9]{0,2})?"
                              step="0.01"
                              className="w-24 border border-gray-500 rounded-md px-2 py-1 text-sm bg-gray-600 text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                              disabled={log.status !== "pending" || !editMode}
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            {item.uom}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            ${(item.unitCost || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            ${siteRate}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-200">
                            $
                            {(
                              parseFloat(
                                quantities[lineItemId] ??
                                  item.qtyInstalled ??
                                  0,
                              ) * (item.unitCost || 0)
                            ).toFixed(2)}
                          </td>

                          <td className="px-4 py-2 text-sm text-gray-200">
                            $
                            {qty > 0
                              ? (parseFloat(siteRate) * qty).toFixed(2)
                              : "0.00"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="11"
                        className="px-4 py-4 text-center text-sm text-gray-400"
                      >
                        No line items found
                        {bidAreaFilter
                          ? ` for bid area "${bidAreaFilter}"`
                          : ""}
                        .
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-4 space-x-4">
              <p className="text-lg font-semibold text-gray-200">
                Payroll Total: $
                {Number(totalPayroll).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-lg font-semibold text-gray-200">
                Total Burden: $
                {Number(totalCost).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>

            {(log.status === "pending" || log.status === "rejected") && (
              <div className="flex justify-end space-x-4 mt-6">
                {editMode ? (
                  <>
                    <button
                      onClick={handleSaveChanges}
                      className="px-4 py-2 text-sm font-semibold text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setSelectedLineItems([]);
                        setQuantities((prev) => {
                          const resetQuantities = {};
                          log.lineItems?.forEach((item) => {
                            const lineItemId =
                              item.lineItemId.$oid || item.lineItemId;
                            resetQuantities[lineItemId] =
                              item.qtyInstalled || 0;
                          });
                          return resetQuantities;
                        });
                      }}
                      className="px-4 py-2 text-sm font-semibold text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {log.status === "pending" && (
                      <>
                        <button
                          onClick={() => showConfirmationDialog("approved")}
                          className="px-4 py-2 text-sm font-semibold text-gray-200 bg-green-400 rounded-md hover:bg-green-500 transition-colors duration-200"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => showConfirmationDialog("rejected")}
                          className="px-4 py-2 text-sm font-semibold text-gray-200 bg-red-400 rounded-md hover:bg-red-500 transition-colors duration-200"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {log.status === "rejected" && (
                      <button
                        onClick={() => showConfirmationDialog("pending")}
                        className="px-4 py-2 text-sm font-semibold text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                      >
                        Reactivate
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <AlertDialog
          open={dialog.open}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="Confirm"
          cancelText="Cancel"
          onClose={closeDialog}
          onConfirm={dialog.onConfirm}
        />
      </div>
    </div>
  );
};

export default PieceworkerLogDetail;
